/**
 * Image Comparison Slider - draggable divider to compare two images
 *
 * Structure expected:
 * <div class="image-comparison-container {horizontal|vertical}" data-orientation="horizontal">
 *   <div class="image-comparison-wrapper">
 *     <div class="image-comparison-image image-comparison-image-1"><img .../></div>
 *     <div class="image-comparison-image image-comparison-image-2"><img .../></div>
 *     <div class="image-comparison-handle" role="slider" ...></div>
 *     <div class="image-comparison-label image-comparison-label-1">Before</div>
 *     <div class="image-comparison-label image-comparison-label-2">After</div>
 *   </div>
 * </div>
 */

class ImageComparison {
  constructor(container) {
    this.$container = $(container);
    this.$wrapper = this.$container.find('.image-comparison-wrapper');
    this.$image1 = this.$container.find('.image-comparison-image-1');
    this.$image2 = this.$container.find('.image-comparison-image-2');
    this.$handle = this.$container.find('.image-comparison-handle');
    this.orientation = this.$container.data('orientation') || 'horizontal';
    this.position = 50; // percentage
    this.isDragging = false;

    this.bindEvents();
    this.updateSlider(this.position);
  }

  bindEvents() {
    const self = this;

    // Mouse events
    this.$handle.on('mousedown', function(e) {
      e.preventDefault();
      self.isDragging = true;
      self.$container.addClass('is-dragging');
    });

    $(document).on('mousemove', function(e) {
      if (!self.isDragging) return;
      self.updateSlider(self.getPositionFromEvent(e));
    });

    $(document).on('mouseup', function() {
      if (!self.isDragging) return;
      self.isDragging = false;
      self.$container.removeClass('is-dragging');
    });

    // Touch events
    this.$handle.on('touchstart', function(e) {
      e.preventDefault();
      self.isDragging = true;
      self.$container.addClass('is-dragging');
    });

    $(document).on('touchmove', function(e) {
      if (!self.isDragging) return;
      self.updateSlider(self.getPositionFromEvent(e.originalEvent.touches[0]));
    });

    $(document).on('touchend', function() {
      if (!self.isDragging) return;
      self.isDragging = false;
      self.$container.removeClass('is-dragging');
    });

    // Keyboard events
    this.$handle.on('keydown', function(e) {
      const step = e.shiftKey ? 10 : 1;
      let newPos = self.position;

      if (self.orientation === 'horizontal') {
        if (e.key === 'ArrowLeft') newPos -= step;
        else if (e.key === 'ArrowRight') newPos += step;
        else return;
      } else {
        if (e.key === 'ArrowUp') newPos -= step;
        else if (e.key === 'ArrowDown') newPos += step;
        else return;
      }

      e.preventDefault();
      self.updateSlider(Math.max(0, Math.min(100, newPos)));
    });

    // Click/tap on wrapper to jump handle position
    this.$wrapper.on('click', function(e) {
      if ($(e.target).closest('.image-comparison-handle').length) return;
      self.updateSlider(self.getPositionFromEvent(e));
    });

    // Prevent touch scrolling on wrapper
    this.$wrapper.css('touch-action', 'none');
  }

  getPositionFromEvent(e) {
    const rect = this.$wrapper[0].getBoundingClientRect();
    if (this.orientation === 'horizontal') {
      return ((e.clientX - rect.left) / rect.width) * 100;
    } else {
      return ((e.clientY - rect.top) / rect.height) * 100;
    }
  }

  updateSlider(position) {
    this.position = Math.max(0, Math.min(100, position));
    const pct = this.position;

    if (this.orientation === 'horizontal') {
      this.$image2.css('clip-path', 'inset(0 0 0 ' + pct + '%)');
      this.$handle.css('left', pct + '%');
    } else {
      this.$image2.css('clip-path', 'inset(0 0 ' + (100 - pct) + '% 0)');
      this.$handle.css('top', pct + '%');
    }

    this.$handle.attr('aria-valuenow', Math.round(pct));
  }
}

/**
 * Initialize image comparison sliders
 * @param {string} selector - jQuery selector for the container(s)
 */
export function initImageComparison(selector) {
  $(selector).each(function() {
    if ($(this).data('image-comparison-initialized')) return;
    $(this).data('image-comparison-initialized', true);
    new ImageComparison(this);
  });
}
